import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, forkJoin, map, startWith, switchMap, takeUntil } from 'rxjs';
import { FormulaireCallCompletion } from '../settings-form.component';

import naceData from '@assets/data/nace.json';
import { ToastService } from '@services/common/toast.service';
import { UserAction, UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { Country as CountryType } from 'app/@types/shared/world-cities';
import countryData from '@assets/data/countries.json';
import { UtilsService } from '@services/common/utils.service';
import { IndustryService } from '@services/common/industry.service';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { SelectItemGroup } from 'primeng/api';

export interface NaceCode {
    id: string;
    label: string;
    translationKey: string;
    industrySectorLabel: string;
    industrySectorId: string;
}

interface Country {
    code: string;
    name: string;
}

@Component({
    selector: 'app-settings-form-organization',
    templateUrl: './settings-form-organization.component.html',
    styleUrls: ['./settings-form-organization.component.scss'],
})
export class SettingsFormOrganizationComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$: Observable<UserClaims | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$: Observable<QuestionnaireDto>;
    @Input() isOnboarding: boolean = false;
    @Output() onboardedEvent: EventEmitter<any> = new EventEmitter();

    public organizationForm!: FormGroup;
    public activities: NaceCode[];
    public selectedIndustryActivity?: NaceCode;

    private ngUnsubscribe = new Subject<void>();
    public userClaim: UserClaims;
    public questionnaire: QuestionnaireDto;
    public loading: boolean = false;
    public countries: Country[] = [];
    public isOrgOwner: boolean = false;

    public isAdminForm: boolean = false;

    vatPlaceholder = 'BE0123.456.789';

    public industries: IndustryDto[] = [];
    private industries$ = this.industryService.get();

    groupedNace: SelectItemGroup[];

    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private utilsService: UtilsService,
        private featureService: FeatureService,
        private readonly industryService: IndustryService,
    ) {
        this.organizationForm = this.formBuilder.group({
            companyLogo: new FormControl(null),
            companyName: new FormControl('', Validators.required),
            vatNumber: new FormControl(''),
            naceCode: new FormControl(''),
            industryCode: new FormControl('', Validators.required),
            fte: new FormControl(1, Validators.min(1)),
            country: new FormControl(''),
        });
    }

    ngOnInit(): void {
        combineLatest([
            this.questionnaire$,
            this.userClaims$,
            this.translateService.onLangChange.pipe(startWith(null)),
            this.industries$,
        ])
            .pipe(
                takeUntil(this.ngUnsubscribe),

                switchMap(([quest, uc, lang, industries]) => {
                    // Create an array of observables for the translations
                    const translationObservables = naceData.map((item) => {
                        const labelTranslation$ = this.translateService.get(`nace.${item.translationKey}`);
                        const industrySectorLabelTranslation$ = this.translateService.get(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        );

                        // Use forkJoin to combine the two translations for each item
                        return forkJoin([labelTranslation$, industrySectorLabelTranslation$]).pipe(
                            map(([labelTranslation, industrySectorLabelTranslation]) => ({
                                ...item,
                                label: this.utilsService.handleTranslation(labelTranslation),
                                industrySectorLabel:
                                    this.utilsService.handleTranslation(industrySectorLabelTranslation),
                            })),
                        );
                    });

                    // Use forkJoin to wait for all translation observables to complete
                    return forkJoin(translationObservables).pipe(
                        map((translatedActivities) => ({
                            quest,
                            uc,
                            lang,
                            translatedActivities,
                            industries,
                        })),
                    );
                }),
            )
            .subscribe(({ quest, uc, lang, translatedActivities, industries }) => {
                this.activities = translatedActivities;

                this.industries = industries
                    .map((item) => ({
                        ...item,
                        label: this.translateService.instant(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        ),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.countries = [];
                this.translateService.get('countries').subscribe((countries) => {
                    Object.entries(countries).forEach(([code, name]) => {
                        this.countries.push({ code: code, name: name });
                    });
                    this.countries.sort((a, b) => a.name.localeCompare(b.name));
                });

                if (uc && uc.organization && quest) {
                    this.organizationForm.enable();
                    this.userClaim = uc;
                    if (uc.organization) {
                        this.isOrgOwner = uc.tasks.some(
                            (task) => task.name === 'orguser' && task.organization_id === uc.organization?.uid,
                        );
                    }
                    if (!this.isOrgOwner) {
                        this.organizationForm.disable();
                    }
                    this.questionnaire = quest;

                    //todo move this assignment to another callback or merge
                    [this.selectedIndustryActivity] = this.activities.filter(
                        (code) => code.industrySectorId === uc.organization?.industryCode,
                    );
                    this.organizationForm.get('companyLogo')!.setValue(uc.organization.avatar);
                    this.organizationForm.get('companyName')!.setValue(uc.organization.name);
                    this.organizationForm.get('vatNumber')!.setValue(uc.organization.tva);
                    this.organizationForm.get('naceCode')!.setValue(uc.organization.nace);
                    this.organizationForm.get('industryCode')!.setValue(uc.organization.industryCode);
                    this.organizationForm.get('country')!.setValue(uc.organization.country);
                    this.organizationForm.markAsPristine();
                    const [matching, nonMatching] = translatedActivities.reduce(
                        ([matches, nonMatches], nace) => {
                            if (nace.industrySectorId === uc.organization?.industryCode) {
                                matches.push(nace);
                            } else {
                                nonMatches.push(nace);
                            }
                            return [matches, nonMatches];
                        },
                        [[], []] as [any[], any[]],
                    );
                    console.log(matching.length, nonMatching.length);
                    // Sort both matching and non-matching arrays alphabetically
                    matching.sort((a, b) => a.label.localeCompare(b.label));
                    nonMatching.sort((a, b) => a.label.localeCompare(b.label));
                    this.groupedNace = [
                        {
                            label: 'Suggested',
                            value: 'suggested',
                            items: matching,
                        },
                        {
                            label: 'ALL',
                            value: 'all',
                            items: nonMatching,
                        },
                    ];
                } else {
                    this.organizationForm.disable();
                }
            });

        this.initializeFormValueSubscriptions();
    }

    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }
    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }
    onCountryChange(event) {
        if (event.value && event.value !== '') {
            const country: CountryType | undefined = countryData.find((country) => country.id === event.value);
            const vatNumberControl = this.organizationForm.get('vatNumber');
            vatNumberControl?.setValue('');
            vatNumberControl?.clearValidators();
            vatNumberControl?.markAsPristine();
            vatNumberControl?.setErrors(null);
            this.vatPlaceholder = 'BE0123.456.789';
            if (country) {
                if (country.regexVaT && country.vatErrorMessage && country.placeholderVAT) {
                    this.organizationForm
                        .get('vatNumber')
                        ?.addValidators(
                            CustomValidators.regexValidator(new RegExp(country.regexVaT), country.vatErrorMessage),
                        );
                    this.vatPlaceholder = country.placeholderVAT;
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        let questionnaireUpdateResult: Subject<FormulaireCallCompletion>;
        this.organizationForm.markAllAsTouched();
        if (
            !this.isAdminForm &&
            this.userClaim.organization &&
            this.organizationForm.valid &&
            (!this.organizationForm.pristine || (this.organizationForm.pristine && this.isOnboarding))
        ) {
            const org = this.userClaim.organization;
            org.avatar = this.organizationForm.get('companyLogo')?.value;
            org.name = this.organizationForm.get('companyName')?.value;
            org.tva = this.organizationForm.get('vatNumber')?.value;
            org.nace = this.organizationForm.get('naceCode')?.value;
            org.industryCode = this.organizationForm.get('industryCode')?.value;
            org.isOnboarded = true;
            org.country = this.organizationForm.get('country')?.value;

            questionnaireUpdateResult = new Subject<FormulaireCallCompletion>();
            this.store.dispatch(new UserAction.UpdateOrganization(org, this.isOnboarding, questionnaireUpdateResult));
            combineLatest([questionnaireUpdateResult])
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (combo) => {
                        if (combo[0] === 'orgSuccess')
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.companySection.toasts.updateSuccess',
                            });
                        if (combo[0] === 'orgFail')
                            this.toastService.showError({
                                details: 'components.settingsForm.companySection.toasts.updateError',
                            });
                    },
                });
        }
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationForm.get('industryCode')!.valueChanges.subscribe((selectedIndustry) => {
            if (selectedIndustry) {
                const [matching, nonMatching] = this.activities.reduce(
                    ([matches, nonMatches], nace) => {
                        if (nace.industrySectorId === selectedIndustry) {
                            matches.push(nace);
                        } else {
                            nonMatches.push(nace);
                        }
                        return [matches, nonMatches];
                    },
                    [[], []] as [any[], any[]],
                );

                matching.sort((a, b) => a.label.localeCompare(b.label));
                nonMatching.sort((a, b) => a.label.localeCompare(b.label));

                this.groupedNace = [
                    {
                        label: 'Suggested',
                        value: 'suggested',
                        items: matching,
                    },
                    {
                        label: 'ALL',
                        value: 'all',
                        items: nonMatching,
                    },
                ];
            }
        });
    }
}
